import React from 'react';
import { Award, Users, Globe, Heart } from 'lucide-react';
import { Card } from '../components/ui/card';
import { useT } from '../lib/i18n';

const About = () => {
  const { t } = useT();
  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "We maintain the highest standards in authenticating art and antiques."
    },
    {
      icon: Users,
      title: "Expertise",
      description: "Our team of specialists brings decades of combined experience in Art, Antiques and Art Deco."
    },
    {
      icon: Globe,
      title: "International",
      description: "With our French and Irish locations, we serve collectors across Europe and beyond."
    },
    {
      icon: Heart,
      title: "Passion",
      description: "Our love for the arts and antiques world drives everything we do."
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-ivory">
        <div className="absolute inset-0">
          <img
            src="/about-hero.png"
            alt="Gallery Interior"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ivory/80 to-cream"></div>
        </div>
        
        <div className="relative z-10 text-center px-6">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
          <h1 className="text-7xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
            {t('about.title')}
          </h1>
          <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6"></div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 px-6 bg-cream deco-pattern">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-8 border-2 border-dark-crimson"></div>
            <h2 className="text-5xl font-bold text-dark-green mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Story
            </h2>
            <div className="h-1 w-32 bg-dark-crimson mx-auto mb-10"></div>
          </div>
          
          <div className="space-y-8 text-charcoal text-xl leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <p>
              David O'Shea is the epitome of a person who does something for the love of it. He is an ex-army man who has had a life-long love of art.
            </p>
            <p>
              Dave O'Shea opened Chimera Gallery in Mullingar, Co. Westmeath, Ireland in 2014, principally to feed his own passion for art — which was ignited when, as a young soldier, he visited the Hermitage Museum in St Petersburg, Russia, with his troop in 1986.
            </p>
            <p className="italic text-dark-green">
              "I wandered into one of the vast rooms with epic paintings adorning the walls. That was the spark. During the following years, I served overseas eight times and I tried to see as many art galleries, shows, and exhibitions as possible, all in secret. Not that I was ashamed; I just thought no one would share my interest."
            </p>
            <p className="italic text-dark-green">
              "After ten-plus successful years in Mullingar, I am now in France and starting again. Though I have many of the artists I started out with, a new crop are on the horizon."
            </p>
            <p>
              Now also delving into antiques and the Art Deco period, he is looking forward to this new chapter.
            </p>
            <p>
              This new chapter will also involve sourcing new items for{' '}
              <a
                href="https://www.instagram.com/bazaarantiques1879/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-dark-green underline decoration-gold/40 underline-offset-4 hover:text-dark-crimson hover:decoration-dark-crimson transition-colors"
              >
                Bazaarantiques
              </a>
              {' '}— a wonderful new establishment owned and run by long-time friend John McCormack. John has vast knowledge of antiques, glass and silverware, and the future collaboration promises to be fun.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 bg-ivory">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-8 border-2 border-dark-crimson"></div>
            <h2 className="text-5xl font-bold text-dark-green mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Values
            </h2>
            <div className="h-1 w-32 bg-dark-crimson mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="bg-cream border-2 border-gold/40 p-10 text-center hover:border-dark-crimson transition-all duration-300 group hover:shadow-xl">
                  <div className="w-20 h-20 bg-dark-crimson mx-auto mb-8 flex items-center justify-center group-hover:bg-gold transition-colors duration-300 border-2 border-gold">
                    <IconComponent className="text-cream" size={36} />
                  </div>
                  <h3 className="text-2xl font-bold text-dark-green mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {value.title}
                  </h3>
                  <p className="text-charcoal leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}>{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 px-6 bg-cream deco-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-3 h-3 rotate-45 bg-gold mb-8 border-2 border-dark-crimson"></div>
              <h2 className="text-5xl font-bold text-dark-green mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                The Art Deco Era
              </h2>
              <div className="h-1 w-32 bg-dark-crimson mb-10"></div>
              <p className="text-charcoal text-xl leading-relaxed mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Art Deco represented a revolutionary shift in design philosophy, characterized by bold geometric 
                patterns, rich colors, and luxurious materials. It embodied the optimism and glamour of the 
                Roaring Twenties while embracing modernism and technological progress.
              </p>
              <p className="text-charcoal text-xl leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                From the Chrysler Building to Tamara de Lempicka's portraits, Art Deco left an indelible mark 
                on architecture, visual arts, fashion, and decorative objects. Today, these pieces continue to 
                captivate collectors with their timeless elegance and craftsmanship.
              </p>
            </div>
            <div className="relative h-[500px] border-4 border-gold">
              <img
                src="/artdeco-era.png"
                alt="Art Deco Era"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-full h-full border-4 border-dark-crimson -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Visit Section */}
      <section className="py-24 px-6 bg-ivory">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-8 border-2 border-dark-crimson"></div>
          <h2 className="text-5xl font-bold text-dark-green mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Visit Us
          </h2>
          <div className="h-1 w-32 bg-dark-crimson mx-auto mb-10"></div>
          <p className="text-charcoal text-xl leading-relaxed mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            We welcome visitors by appointment at both our French and Irish locations. Our knowledgeable staff 
            is available to guide you through our collections and assist with acquisitions, valuations, and 
            restoration services.
          </p>
          <a href="/contact">
            <button className="bg-dark-crimson hover:bg-crimson text-cream px-12 py-6 rounded-none border-2 border-gold transition-all duration-300 hover:scale-105 font-semibold tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>
              Schedule a Visit
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
